/**
 * Stacked bootstrap modal that opens automatically and closes on successful form submission.
 */
import { Controller } from "@hotwired/stimulus";
import { Modal } from "bootstrap";

// Matches three record-show URL shapes:
//   /<role>/records/<resource>/<id>          e.g. /admin/records/students/a1b2c3
//   /<role>/<resource>/<id>                  e.g. /admin/published_sources/a1b2c3
//   /niiwin/<workspace>/<resource>/<id>      e.g. /niiwin/ns_data/files/a1b2c3
// where <role> can be [admin, data_librarian, researcher, junior_researcher]
const showUrlRegex =
  /\/(?:(?:admin|data_librarian|researcher|junior_researcher)\/(?:records\/\w{2,}|(?!records\b)\w{2,})|niiwin\/\w{2,}\/\w{2,})\/[\d\w-]+$/;

export default class extends Controller {
  static get targets() {
    return ["autofocus"];
  }

  static values = {
    readonly: { type: Boolean, default: false },
  };

  initialize() {
    // Store URL, method, and original target of the most recent Turbo fetch.
    // `stashFetchUrl` stashes the values and `hideBeforeRender` checks these
    // to decide whether the turbostream is from a stacking show GET
    // (leave alone) or an edit GET / form submission (we use bootstrap's
    // hide() so the body scrolling is cleaned up).
    this.lastFetchUrl = null;
    this.lastFetchMethod = "GET";
    this.lastFetchTarget = null;
    this.lastFetchStatus = null;

    this.element.addEventListener("shown.bs.modal", (event) => {
      const modal = event.target;
      const modalId = modal.getAttribute("id");

      modal.querySelectorAll("a").forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set("last_modal_id", modalId);
        if (this.readonlyValue) {
          url.searchParams.set("readonly", "true");
        }
        link.href = url.toString();
      }, this);

      if (this.hasAutofocusTarget) {
        setTimeout(() => {
          this.autofocusTarget.focus();
        }, 500);
      } else {
        this.element.focus();
      }
    });

    // Clean up existing modal by emptying its content
    this.element.addEventListener("hidden.bs.modal", () => {
      const emptyModalDiv = document.createElement("div");
      emptyModalDiv.id = this.element.id;
      this.element.replaceWith(emptyModalDiv);
    });
  }

  connect() {
    this.modal = new Modal(this.element);
    this.modal.show();
  }

  disconnect() {
    this.modal.dispose();
  }

  // Connected to `turbo:before-fetch-request@document`
  stashFetchUrl(event) {
    this.lastFetchUrl = event.detail.url || null;
    this.lastFetchMethod = event.detail.fetchOptions?.method || "GET";
    this.lastFetchTarget = event.target || null;
    this.lastFetchStatus = null;
  }

  // Connected to `turbo:before-fetch-response@document`.
  // Capture the response status so `hideBeforeRender` can differentiate a
  // successful submission (close the modal) from a validation failure (422,
  // keep the modal open so form validation errors can be shown).
  stashFetchResponse(event) {
    this.lastFetchStatus = event.detail.fetchResponse?.response?.status || null;
  }

  // Connected to `turbo:before-stream-render@document`.
  // Show GETs stack a new modal on top of the current one so we do not hide.
  // We do not need stacking for edit modals and form submissions (non-GET)
  // so we do the following:
  // - pause it
  // - hide the modal cleanly so Bootstrap runs its body-scroll cleanup
  // - re-apply the stream via Turbo.renderStreamMessage once `hidden.bs.modal` fires
  hideBeforeRender(event) {
    if (!this.modal?._element) return;
    if (!this.element.classList.contains("show")) return;
    if (!this.lastFetchUrl || this.isRecordShowRequest(this.lastFetchUrl, this.lastFetchMethod)) return;
    if (this.lastFetchStatus && this.lastFetchStatus >= 400) return;

    // Ensure submission is relevant to the current modal by checking `last_modal_id`
    const lastModalIdInput = this.lastFetchTarget?.last_modal_id;
    if (lastModalIdInput != null && lastModalIdInput.value !== this.element.id) return;

    event.preventDefault();
    const streamHtml = event.target.outerHTML;
    this.element.addEventListener("hidden.bs.modal", () => Turbo.renderStreamMessage(streamHtml), { once: true });
    this.modal.hide();
  }

  isRecordShowRequest(url, method = "GET") {
    if (method.toUpperCase() !== "GET") return false;
    return url ? showUrlRegex.test(url.pathname) : false;
  }
}
